import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useAppSelector } from "../../../hooks/hooks";
import {
  selectLanguage,
  selectTranslations,
} from "../../../shared/data-access/store/general/generalSlice";
import { checkTranslationKey } from "../../../utils/translation";
import {
  StreakState,
  StreakWeekDay,
} from "../../../shared/interfaces/streak.types";
import "./streak-card.scss";

export interface StreakCardProps {
  streak?: StreakState | null;
}

/**
 * The home dashboard streak widget (FR-20 to FR-23).
 *
 * Collapsed it shows the count, the next milestone and its reward; expanded it adds the week's
 * tracker. Selecting the card itself opens the standalone streak page, so the chevron stops
 * propagation - it only toggles.
 */
export function StreakCard({ streak }: StreakCardProps) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const translations = useAppSelector(selectTranslations);
  const lang = useAppSelector(selectLanguage);

  if (!streak?.active) {
    return null;
  }

  const { current, next_milestone: next, completed, week } = streak;
  const daysThisWeek = week.filter((day) => day.qualified).length;
  // The week is ordered Monday first and `future` flips after today, so the last non-future entry
  // is today. Today not yet played reads differently from a day that was genuinely missed.
  const todayInWeek = week.filter((day) => !day.future).slice(-1)[0]?.day;
  // D6: past the final milestone there is no next one, so the progress line is replaced by the
  // completion message rather than rendering "Day -" or a 31/30 bar
  const progress = next ? Math.min(100, (current / next.day) * 100) : 100;

  const toggle = (event: React.MouseEvent) => {
    event.stopPropagation();
    setExpanded((value) => !value);
  };

  return (
    <div className="streak-card" onClick={() => navigate("/streak")}>
      <img className="streak-flame" src="/img/streak/flame-big.png" alt="" />

      <div className="streak-count">
        <div className="count">{current}</div>
        <div className="label">
          {checkTranslationKey(translations[lang]?.Day_streak, "DAY STREAK")}
        </div>
      </div>

      {completed || !next ? (
        <div className="streak-complete">
          {checkTranslationKey(
            translations[lang]?.Streak_ladder_complete,
            "All milestones complete. Keep the fire going!"
          )}
        </div>
      ) : (
        <div className="streak-progress">
          <div className="progress-labels">
            <span className="next-milestone">
              {checkTranslationKey(
                translations[lang]?.Next_milestone,
                "Next milestone"
              )}
              : {checkTranslationKey(translations[lang]?.Day, "Day")} {next.day}
            </span>
            <span className="progress-count">
              {current} / {next.day}
            </span>
          </div>

          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>

          <div className="progress-reward">
            <div className="reward">
              <span className="reward-label">
                {checkTranslationKey(translations[lang]?.Reward, "Reward")}:
              </span>
              <span className="reward-amount">{next.diamonds}</span>
              <img src="/img/icns/diamonds.png" alt="" />
              {next.coins > 0 && (
                <>
                  <span className="reward-plus">+</span>
                  <span className="reward-amount">{next.coins}</span>
                  <img src="/img/icns/coins.png" alt="" />
                </>
              )}
            </div>
            <button
              className={`chevron ${expanded ? "" : "chevron-down"}`}
              onClick={toggle}
              aria-label={checkTranslationKey(
                translations[lang]?.Show_this_week,
                "Show this week"
              )}
            >
              <img src="/img/streak/chevron.svg" alt="" />
            </button>
          </div>
        </div>
      )}

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            className="streak-week"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="week-divider" />
            <div className="week-summary">
              {daysThisWeek}{" "}
              {checkTranslationKey(
                translations[lang]?.Days_completed_this_week,
                "days completed this week. Keep it up!"
              )}
            </div>
            <div className="week-days">
              {week.map((day) => (
                <WeekDay key={day.day} day={day} today={todayInWeek} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function WeekDay({ day, today }: { day: StreakWeekDay; today?: string }) {
  const state = day.qualified
    ? "lit"
    : day.future
    ? "locked"
    : day.day === today
    ? "today"
    : "missed";

  return (
    <div className="week-day">
      <div className={`day-icon day-${state}`}>
        {state === "lit" && <img src="/img/streak/flame-lit.png" alt="" />}
        {(state === "today" || state === "missed") && (
          <img src="/img/streak/flame-today.svg" alt="" />
        )}
        {state === "locked" && <img src="/img/streak/lock.svg" alt="" />}
      </div>
      <div className="day-label">{day.weekday.charAt(0)}</div>
    </div>
  );
}
